import React, { useEffect, useRef, useState } from "react";
import { X, RefreshCw, CalendarClock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import DateInput from "../../components/DateInput";
import { fmtDateGB } from "../../lib/format";

/**
 * Sync history / backfill dialog.
 *
 * Two-step flow:
 *   1) Detect gaps in the last N days (default 90) — shows a list of dates
 *      with < 40 samples so the operator can see exactly what's missing.
 *   2) Pick a date range and either "fill gaps only" (idempotent, cheap)
 *      or "force re-fetch" (full replace). Backfill is chunked into
 *      28-day windows on the backend to stay under MeterOnline's per-request
 *      1-year cap.
 */
export default function SyncHistoryDialog({ projectId, onClose, onDone }) {
  const [lookback, setLookback] = useState(90);
  const [gaps, setGaps] = useState(null);
  const [loadingGaps, setLoadingGaps] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const ninetyDaysAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(ninetyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [fillGapsOnly, setFillGapsOnly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Guard against overlapping gap-scan calls when the user rapidly changes
  // the lookback dropdown — only the latest request is allowed to write
  // state; earlier ones are discarded.
  const scanIdRef = useRef(0);

  const detectGaps = async () => {
    const myId = ++scanIdRef.current;
    setLoadingGaps(true);
    try {
      const { data } = await api.get(
        `/monitoring/${projectId}/gaps?lookback_days=${lookback}`,
      );
      if (myId !== scanIdRef.current) return;   // stale — a newer request already superseded us
      setGaps(data);
    } catch (e) {
      if (myId !== scanIdRef.current) return;
      toast.error(formatApiError(e.response?.data?.detail) || "Gap scan failed");
    } finally {
      if (myId === scanIdRef.current) setLoadingGaps(false);
    }
  };

  useEffect(() => { detectGaps(); /* eslint-disable-next-line */ }, [projectId, lookback]);

  const runBackfill = async () => {
    if (!startDate || !endDate) return toast.error("Pick a start and end date");
    if (endDate < startDate) return toast.error("End date must be on or after start date");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(
        `/monitoring/${projectId}/backfill-range`,
        { start_date: startDate, end_date: endDate, fill_gaps_only: fillGapsOnly },
      );
      setResult(data);
      if (data.errors && data.errors.length > 0) {
        toast.warning(`Backfill finished with ${data.errors.length} error(s) — see details below`);
      } else {
        toast.success(`Wrote ${data.written.toLocaleString()} samples across ${data.days_fetched} day(s)`);
      }
      // Refresh the gap list so the user sees the effect
      detectGaps();
      if (onDone) onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Backfill failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/45 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="sync-history-dialog">
      <div className="bg-white w-full max-w-3xl border border-[var(--line)]"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="label flex items-center gap-2">
              <CalendarClock size={11} /> Sync history · backfill missing data
            </div>
            <div className="font-display text-xl font-semibold mt-1">
              Detect gaps &amp; backfill
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100"
                  data-testid="sync-history-close-btn"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Gap detection */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="label">1. Gap scan</div>
              <div className="flex items-center gap-2">
                <label className="text-[11px] muted">Look back</label>
                <select className="input-swiss text-xs py-1"
                        value={lookback}
                        onChange={(e) => setLookback(Number(e.target.value))}
                        data-testid="gap-lookback-select">
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={365}>1 year</option>
                </select>
                <button className="btn-ghost text-[10px]"
                        onClick={detectGaps} disabled={loadingGaps}
                        data-testid="gap-rescan-btn">
                  <RefreshCw size={11} /> Re-scan
                </button>
              </div>
            </div>

            {loadingGaps ? (
              <div className="text-xs muted">Scanning…</div>
            ) : gaps ? (
              gaps.days_with_gaps === 0 ? (
                <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2"
                     data-testid="gap-none">
                  <CheckCircle2 size={14} />
                  No gaps in the last {lookback} days — every day has ≥ 40 samples.
                </div>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200"
                     data-testid="gap-list">
                  <div className="text-xs text-amber-900 flex items-center gap-2 mb-2 font-mono">
                    <AlertTriangle size={13} />
                    {gaps.days_with_gaps} day(s) with missing HH data
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {gaps.gaps.map((g) => (
                      <span key={g.date}
                            className="text-[10px] font-mono px-1.5 py-0.5 bg-white border border-amber-300 text-amber-900"
                            title={`${g.sample_count} of 96 samples`}>
                        {fmtDateGB(g.date)} <span className="muted">({g.sample_count})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )
            ) : null}
          </section>

          {/* Backfill controls */}
          <section>
            <div className="label mb-3">2. Backfill range</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="label mb-1 block">Start date</span>
                <DateInput className="input-swiss w-full font-mono text-xs"
                       value={startDate}
                       onChange={(e) => setStartDate(e.target.value)}
                       max={today}
                       data-testid="backfill-start-date" />
              </label>
              <label className="block">
                <span className="label mb-1 block">End date</span>
                <DateInput className="input-swiss w-full font-mono text-xs"
                       value={endDate}
                       onChange={(e) => setEndDate(e.target.value)}
                       max={today}
                       data-testid="backfill-end-date" />
              </label>
            </div>

            <label className={`mt-3 flex items-start gap-3 p-3 border cursor-pointer transition-colors
                              ${fillGapsOnly ? "border-[var(--line)] bg-[var(--surface-2)]" : "border-amber-300 bg-amber-50"}`}
                   data-testid="backfill-mode-row">
              <input type="checkbox" className="mt-0.5" checked={fillGapsOnly}
                     onChange={(e) => setFillGapsOnly(e.target.checked)}
                     data-testid="backfill-fill-gaps-only" />
              <div className="flex-1">
                <div className="font-mono text-xs uppercase tracking-widest">
                  {fillGapsOnly ? "Fill gaps only" : "Force full re-fetch"}
                </div>
                <div className={`text-[11px] mt-1 ${fillGapsOnly ? "muted" : "text-amber-800"}`}>
                  {fillGapsOnly
                    ? "Skip any day already populated (≥ 40 samples). Fast and idempotent — safe to re-run."
                    : "⚠ Re-fetch every day in the range from the vendor. Uses more API quota; existing samples are overwritten."}
                </div>
              </div>
            </label>
          </section>

          {/* Result */}
          {result && (
            <section className="p-4 border border-[var(--line)] bg-[var(--surface-2)]"
                     data-testid="backfill-result">
              <div className="label mb-2">Last run</div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="muted">Written</div>
                  <div className="font-mono text-lg">{result.written?.toLocaleString?.() || 0}</div>
                </div>
                <div>
                  <div className="muted">Days fetched</div>
                  <div className="font-mono text-lg">{result.days_fetched} / {result.days_requested}</div>
                </div>
                <div>
                  <div className="muted">Errors</div>
                  <div className={`font-mono text-lg ${result.errors?.length ? "text-red-600" : ""}`}>
                    {result.errors?.length || 0}
                  </div>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <ul className="mt-3 text-[11px] font-mono text-red-700 list-disc pl-4 max-h-24 overflow-y-auto">
                  {result.errors.slice(0, 10).map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              )}
            </section>
          )}
        </div>

        <div className="flex justify-end items-center gap-2 p-5 border-t border-[var(--line)] bg-[var(--surface-2)]">
          <button onClick={onClose} className="btn-ghost"
                  data-testid="sync-history-cancel-btn">Close</button>
          <button onClick={runBackfill} disabled={busy}
                  className="btn-primary bg-[var(--accent)] border-[var(--accent)]"
                  data-testid="run-backfill-btn">
            <RefreshCw size={13} /> {busy ? "Backfilling…" : "Run backfill"}
          </button>
        </div>
      </div>
    </div>
  );
}
